import {
  PACK_SUBMISSION_BULK_MAX_IDS,
  type PackSubmission,
  type StaffRole,
} from '@planuze/admin-contracts';
import {
  Button,
  InlineAlert,
  Page,
  PageContent,
  PageDescription,
  PageHeader,
  PageTitle,
  PageTitleBlock,
  TableSkeleton,
  toast,
} from '@planuze/ui';
import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { ModerationMetricsGrid } from '../components/pack-submissions/moderation-metrics-grid.js';
import { availableActions } from '../components/pack-submissions/pack-submission-status.js';
import { PackSubmissionsBulkBar } from '../components/pack-submissions/pack-submissions-bulk-bar.js';
import {
  defaultPackSubmissionsFilters,
  PackSubmissionsFilters,
  periodToSubmittedAfter,
} from '../components/pack-submissions/pack-submissions-filters.js';
import { PackSubmissionsTable } from '../components/pack-submissions/pack-submissions-table.js';
import {
  ACTION_CONFIG,
  type DialogAction,
} from '../components/pack-submissions/submission-action-config.js';
import { ReasonDialog } from '../components/reason-dialog.js';
import { usePackSubmissionsContent } from '../content/sections/pack-submissions.js';
import { usePackSubmissionsListContent } from '../content/sections/pack-submissions-list.js';
import { useDebounce } from '../hooks/use-debounce.js';
import { useLimitedBulkSelection } from '../hooks/use-limited-bulk-selection.js';
import { useModerationMetrics } from '../hooks/use-metrics.js';
import {
  type SubmissionAction,
  useBulkPackSubmissionAction,
  usePackSubmissionAction,
  usePackSubmissionsList,
} from '../hooks/use-pack-submissions.js';
import { useStaffNotify } from '../hooks/use-staff-notify.js';
import { useStaffSession } from '../hooks/use-staff-session.js';
import type { PackSubmissionsQuery } from '../lib/pack-submissions-api.js';

/* Debounce dos filtros textuais server-side — evita refetch por tecla (molde de `audit-page`). */
const FILTER_DEBOUNCE_MS = 300;
const MANAGE_ROLES: readonly StaffRole[] = ['super_admin', 'support'];

type PendingAction =
  | {
      readonly kind: 'single';
      readonly submission: PackSubmission;
      readonly action: DialogAction;
    }
  | {
      readonly kind: 'bulk';
      readonly ids: readonly string[];
      readonly action: SubmissionAction;
    };

export function PackSubmissionsListPage() {
  const copy = usePackSubmissionsListContent();
  const submissionCopy = usePackSubmissionsContent();
  const navigate = useNavigate();
  const session = useStaffSession();
  const canManage = session !== null && MANAGE_ROLES.includes(session.role);
  /* Realtime da fila (TASK-PUB-50): nova submissão OU decisão de outro staff invalida a lista
     via WS → sem ctrl+r. Best-effort: se o WS cair, o refetch on-focus/on-action cobre. */
  useStaffNotify();
  const { data: moderationMetrics } = useModerationMetrics();
  const [filters, setFilters] = useState(defaultPackSubmissionsFilters);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const action = usePackSubmissionAction();
  const bulkAction = useBulkPackSubmissionAction();

  /* start-review/reopen (sem motivo) disparam direto; o resto abre o ReasonDialog. */
  const handleAction = (submission: PackSubmission, act: SubmissionAction): void => {
    if (act === 'start-review' || act === 'reopen') {
      action.mutate(
        { id: submission.id, action: act, reason: '' },
        {
          onSuccess: () =>
            toast.success(act === 'reopen' ? copy.reopenedToast : copy.reviewStartedToast),
          onError: () => toast.error(copy.actionError),
        },
      );
      return;
    }
    setPending({ kind: 'single', submission, action: act });
  };
  /* Publisher é filtro textual server-side → debounce. Janela do período ancorada na SELEÇÃO
     (não em cada render) — senão `Date.now()` mudaria a queryKey a cada frame (refetch loop). */
  const debouncedPublisherId = useDebounce(filters.publisherId, FILTER_DEBOUNCE_MS);
  const submittedAfter = useMemo(() => periodToSubmittedAfter(filters.period), [filters.period]);
  const serverQuery = useMemo<PackSubmissionsQuery>(
    () => ({
      status: filters.status === 'all' ? undefined : filters.status,
      publisherId: debouncedPublisherId.length > 0 ? debouncedPublisherId : undefined,
      scanStatus: filters.scanStatus === 'all' ? undefined : filters.scanStatus,
      submittedAfter,
    }),
    [filters.status, filters.scanStatus, debouncedPublisherId, submittedAfter],
  );
  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    usePackSubmissionsList(serverQuery);
  const items = useMemo(() => data?.pages.flatMap((page) => page.submissions) ?? [], [data]);
  /* Busca por pack = filtro client-side sobre a página carregada (nome/id do pack não é campo do
     contrato). Publisher/scanStatus/período são server-side (particionam a fila inteira). */
  const query = filters.search.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      query.length === 0
        ? items
        : items.filter((submission) => submission.packId.toLowerCase().includes(query)),
    [items, query],
  );
  const actionableIds = useMemo(
    () =>
      filtered.filter((item) => availableActions(item.status).length > 0).map((item) => item.id),
    [filtered],
  );
  const selection = useLimitedBulkSelection(actionableIds, PACK_SUBMISSION_BULK_MAX_IDS);
  const selectedItems = useMemo(
    () => filtered.filter((item) => selection.selectedIds.has(item.id)),
    [filtered, selection.selectedIds],
  );
  const commonActions = useMemo<readonly SubmissionAction[]>(() => {
    const first = selectedItems[0];
    if (first === undefined) return [];
    return availableActions(first.status).filter((candidate) =>
      selectedItems.every((item) => availableActions(item.status).includes(candidate)),
    );
  }, [selectedItems]);
  const hasFilters =
    filters.status !== 'all' ||
    query.length > 0 ||
    debouncedPublisherId.length > 0 ||
    filters.scanStatus !== 'all' ||
    filters.period !== 'all';
  const clearFilters = (): void => setFilters(defaultPackSubmissionsFilters());
  const config = pending?.kind === 'single' ? ACTION_CONFIG[pending.action] : null;
  const isBusy = action.isPending || bulkAction.isPending;

  const requestBulk = (requestedAction: SubmissionAction): void => {
    if (selection.selectedIds.size === 0) return;
    setPending({
      kind: 'bulk',
      ids: [...selection.selectedIds],
      action: requestedAction,
    });
  };

  return (
    <Page>
      <PageHeader>
        <PageTitleBlock>
          <PageTitle>{copy.pageTitle}</PageTitle>
          <PageDescription>{copy.pageDescription}</PageDescription>
        </PageTitleBlock>
      </PageHeader>
      <PageContent className="space-y-4">
        {/* SLA da moderação (ADR-0463): cards no topo da fila que eles medem. */}
        <ModerationMetricsGrid metrics={moderationMetrics} />
        <PackSubmissionsFilters value={filters} onChange={setFilters} />
        {canManage && selection.selectedIds.size > 0 && (
          <PackSubmissionsBulkBar
            selectedLabel={copy.bulk.selectedLabel(selection.selectedIds.size)}
            clearLabel={copy.bulk.clearLabel}
            actions={commonActions}
            actionLabels={submissionCopy.actionLabel}
            busy={isBusy}
            onApply={requestBulk}
            onClear={selection.clear}
          />
        )}
        {isError ? (
          <InlineAlert variant="destructive">{copy.loadError}</InlineAlert>
        ) : isLoading ? (
          <TableSkeleton columns={canManage ? 6 : 4} />
        ) : (
          <PackSubmissionsTable
            items={filtered}
            busyId={action.isPending ? action.variables?.id : undefined}
            busy={isBusy}
            canManage={canManage}
            selectedIds={selection.selectedIds}
            selectAllAriaLabel={copy.bulk.selectAllAriaLabel}
            selectRowAriaLabel={copy.bulk.selectRowAriaLabel}
            hasFilters={hasFilters}
            onClearFilters={clearFilters}
            onToggle={selection.toggle}
            onToggleAll={(ids, checked) => selection.replace(checked ? ids : [])}
            onAction={handleAction}
            onRowClick={(id) => {
              navigate({ to: '/pack-submissions/$id', params: { id } }).catch(() => undefined);
            }}
          />
        )}
        {hasNextPage && (
          <div className="flex justify-center">
            <Button
              variant="ghost"
              loading={isFetchingNextPage}
              onClick={() => {
                fetchNextPage().catch(() => undefined);
              }}
            >
              {copy.loadMoreButton}
            </Button>
          </div>
        )}
      </PageContent>
      {pending?.kind === 'single' && config !== null && (
        <ReasonDialog
          open
          onClose={() => !isBusy && setPending(null)}
          title={config.title}
          description={config.description}
          confirmLabel={config.confirmLabel}
          reasonLabel={config.reasonLabel}
          destructive={config.destructive}
          pending={isBusy}
          validate={config.validate}
          onConfirm={(reason) => {
            action.mutate(
              { id: pending.submission.id, action: pending.action, reason },
              {
                onSuccess: () => {
                  toast.success(copy.submissionUpdatedToast);
                  setPending(null);
                },
                onError: () => toast.error(copy.actionError),
              },
            );
          }}
        />
      )}
      {pending?.kind === 'bulk' && (
        <ReasonDialog
          open
          onClose={() => !isBusy && setPending(null)}
          title={copy.bulk.dialogTitle(
            submissionCopy.actionLabel[pending.action],
            pending.ids.length,
          )}
          description={copy.bulk.dialogDescription}
          confirmLabel={submissionCopy.actionLabel[pending.action]}
          reasonLabel={copy.bulk.reasonLabel}
          destructive={pending.action === 'reject'}
          pending={isBusy}
          validate={(reason) => reason.trim().length > 0}
          onConfirm={(reason) => {
            bulkAction.mutate(
              { ids: [...pending.ids], action: pending.action, reason: reason.trim() },
              {
                onSuccess: (response) => {
                  const failed = response.results.filter((result) => !result.ok).length;
                  if (failed === 0) toast.success(copy.bulk.successToast(response.results.length));
                  else {
                    toast.warning(copy.bulk.partialToast(response.results.length - failed, failed));
                  }
                  selection.clear();
                  setPending(null);
                },
                onError: () => toast.error(copy.bulk.errorToast),
              },
            );
          }}
        />
      )}
    </Page>
  );
}
